import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import { ZxcvbnFactory } from '@zxcvbn-ts/core';
import * as zxcvbnCommon from '@zxcvbn-ts/language-common';
import * as zxcvbnEn from '@zxcvbn-ts/language-en';

import { PASSWORD_REGEX } from '../password-policy';

const zxcvbn = new ZxcvbnFactory({
  dictionary: { ...zxcvbnCommon.dictionary, ...zxcvbnEn.dictionary },
  graphs: zxcvbnCommon.adjacencyGraphs,
  translations: zxcvbnEn.translations,
});

const STRENGTH = [
  { width: '20%', color: '#de1616' },
  { width: '40%', color: '#de1616' },
  { width: '60%', color: '#de1616' },
  { width: '80%', color: '#ffa200' },
  { width: '100%', color: '#06bf06' },
];

function encouragement(score: number, isValid: boolean): string {
  if (!isValid) return 'Keep typing';
  if (score >= 4) return 'Great password';
  if (score === 3) return 'Safe enough';
  return 'Keep typing';
}

interface PasswordStrengthMeterProps {
  password: string;
}

function PasswordStrengthMeter({ password }: PasswordStrengthMeterProps): ReactElement | null {
  const meter = useMemo(() => {
    const trimmed = password.trim();
    if (!trimmed) return null;

    const isValid = PASSWORD_REGEX.test(trimmed) && trimmed.length >= 8;
    const { score: rawScore, feedback } = zxcvbn.check(trimmed);

    // Never advertise a password as strong while it still fails the required rule.
    const score = !isValid && rawScore > 2 ? 2 : rawScore;
    const text = feedback.warning || feedback.suggestions[0] || encouragement(score, isValid);

    return { score, text };
  }, [password]);

  const { width, color } = meter ? STRENGTH[meter.score] : {};

  return (
    <Collapse in={typeof width !== 'undefined'}>
      <Box sx={{ height: 4, borderRadius: 1, backgroundColor: '#e0e0e0', overflow: 'hidden' }}>
        <Box
          sx={{ height: '100%', width, backgroundColor: color, transition: 'width 0.2s ease' }}
        />
      </Box>
      {meter?.text && (
        <Typography variant="caption" sx={{ color }}>
          {meter.text}
        </Typography>
      )}
    </Collapse>
  );
}

export default PasswordStrengthMeter;
